import { type ReactNode, useState, useRef, useCallback, useEffect, useLayoutEffect, memo } from 'react';
import { ChevronUp, ChevronDown, Search } from 'lucide-react';
import clsx from 'clsx';
import { Input } from './Input';
import { ColumnFilter, type ColumnFilterValue } from './ColumnFilter';
import { ContextMenu, type ContextMenuItem } from './ContextMenu';

/**
 * Rows are plain records. The `any` is deliberate: `Record<string, unknown>` would reject
 * every caller's interface, since interfaces have no implicit index signature.
 */
type TableRowData = Record<string, any>;

export interface ColumnFilterConfig {
  values: ColumnFilterValue[];
  selectedValues: string[];
  onChange: (selected: string[]) => void;
}

export interface Column<T> {
  key: string;
  label: string;
  labelRender?: () => ReactNode;
  sortable?: boolean;
  filterable?: boolean;
  filterConfig?: ColumnFilterConfig;
  renderFilter?: () => ReactNode;
  render?: (row: T) => ReactNode;
  /** Optional single-line renderer used when the table is in compact mode. Falls back to `render`. */
  compactRender?: (row: T) => ReactNode;
  width?: string;
}

interface SortConfig {
  key: string;
  direction: 'asc' | 'desc';
}

export interface SelectionConfig<T> {
  /** Returns a stable unique ID for a row */
  getRowId: (row: T) => string;
  selectedIds: Set<string>;
  onToggleRow: (id: string) => void;
  onTogglePage: (ids: string[], allSelected: boolean) => void;
  /** When true all rows across all pages are considered selected */
  allPagesSelected?: boolean;
}

interface TableProps<T> {
  data: T[];
  columns: Column<T>[];
  onRowClick?: (row: T, event: React.MouseEvent) => void;
  /** Fired when a row is clicked while holding Cmd (macOS) or Ctrl. Suppresses the context menu when present. */
  onRowModClick?: (row: T, event: React.MouseEvent) => void;
  searchable?: boolean;
  searchPlaceholder?: string;
  /** A node, so a caller can offer a way out of an empty result (see the Venues pause filter). */
  emptyMessage?: ReactNode;
  // Server-side sort support
  serverSort?: boolean;
  sortConfig?: SortConfig | null;
  onSort?: (key: string, direction: 'asc' | 'desc' | null) => void;
  // Row styling
  rowClassName?: (row: T) => string | undefined;
  // Context menu support
  rowContextMenu?: (row: T) => ContextMenuItem[];
  // Hover tooltip content
  rowHoverContent?: (row: T) => ReactNode | null;
  // Cell right-click support
  onCellRightClick?: (row: T, columnKey: string, position: { x: number; y: number }) => void;
  // Multi-select support
  selection?: SelectionConfig<T>;
  // Compact mode: one single-line row per item, truncating overflow with the full value on hover
  compact?: boolean;
}

/** Wraps a cell's content on a single line, exposing the full text as a native tooltip when it overflows. */
function CompactCell({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  // Set the `title` attribute directly on the node (rather than via state) so it stays
  // in sync after re-renders without triggering cascading React updates.
  useLayoutEffect(() => {
    const el = ref.current;
    if (el) el.title = el.scrollWidth > el.clientWidth ? el.textContent ?? '' : '';
  });
  return (
    <div ref={ref} className="compact-cell">
      {children}
    </div>
  );
}

interface ContextMenuState<T> {
  row: T;
  position: { x: number; y: number };
}

interface TableRowProps<T> {
  row: T;
  index: number;
  columns: Column<T>[];
  compact: boolean;
  isSelected: boolean;
  isFocused: boolean;
  /** Already resolved from the caller's `rowClassName`, so an unchanged row compares equal. */
  className?: string;
  rowId?: string;
  interactive: boolean;
  selectable: boolean;
  hoverable: boolean;
  cellRightClickable: boolean;
  onRowMouseDown: (index: number) => void;
  onRowClick: (row: T, rowId: string | undefined, event: React.MouseEvent) => void;
  onRowMouseEnter: (row: T, index: number) => void;
  onRowMouseMove: (row: T, event: React.MouseEvent) => void;
  onRowMouseLeave: () => void;
  onToggleRow: (rowId: string) => void;
  onCellRightClick: (row: T, columnKey: string, event: React.MouseEvent) => void;
}

/**
 * One `<tr>`, memoized.
 *
 * A row's cells are the expensive part of this table — the Venues page alone renders sixteen
 * columns of tooltips, badges and formatted dates per row, so re-rendering a full page of rows
 * costs hundreds of milliseconds. Table-internal state (which row is focused, whether a context
 * menu is open) changes on every click and touches no row data, so the rows are memoized and
 * everything they receive is kept referentially stable: handlers come from `useCallback` in the
 * parent and read live props through a ref, and `rowClassName` is resolved to a string by the
 * parent so only the rows whose class actually changed re-render.
 */
function TableRowInner<T extends TableRowData>({
  row,
  index,
  columns,
  compact,
  isSelected,
  isFocused,
  className,
  rowId,
  interactive,
  selectable,
  hoverable,
  cellRightClickable,
  onRowMouseDown,
  onRowClick,
  onRowMouseEnter,
  onRowMouseMove,
  onRowMouseLeave,
  onToggleRow,
  onCellRightClick,
}: TableRowProps<T>) {
  return (
    <tr
      className={clsx('table-row', interactive && 'cursor-pointer', className)}
      style={
        isSelected
          ? { backgroundColor: 'rgb(var(--color-primary-900) / 0.25)' }
          : isFocused
          ? { backgroundColor: 'rgb(var(--color-border))' }
          : undefined
      }
      onMouseDown={() => onRowMouseDown(index)}
      onClick={(e) => onRowClick(row, rowId, e)}
      onMouseEnter={hoverable ? () => onRowMouseEnter(row, index) : undefined}
      onMouseMove={hoverable ? (e) => onRowMouseMove(row, e) : undefined}
      onMouseLeave={hoverable ? onRowMouseLeave : undefined}
    >
      {selectable && rowId && (
        <td className="table-cell w-10" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-center">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => onToggleRow(rowId)}
              className="w-3.5 h-3.5 accent-primary-500 cursor-pointer"
            />
          </div>
        </td>
      )}
      {columns.map((column) => {
        const content = (compact && column.compactRender)
          ? column.compactRender(row)
          : column.render
            ? column.render(row)
            : String(row[column.key] ?? '-');
        return (
          <td
            key={column.key}
            className={clsx('table-cell', compact && 'py-1.5')}
            onContextMenu={cellRightClickable ? (e) => onCellRightClick(row, column.key, e) : undefined}
          >
            {compact ? <CompactCell>{content}</CompactCell> : content}
          </td>
        );
      })}
    </tr>
  );
}

// `memo` erases the generic, so the cast hands it back.
const TableRow = memo(TableRowInner) as typeof TableRowInner;

export function Table<T extends TableRowData>({
  data,
  columns,
  onRowClick,
  onRowModClick,
  searchable = true,
  searchPlaceholder = 'Search...',
  emptyMessage = 'No data found',
  serverSort = false,
  sortConfig: externalSortConfig,
  onSort,
  rowClassName,
  rowContextMenu,
  rowHoverContent,
  onCellRightClick,
  selection,
  compact = false,
}: TableProps<T>) {
  const [internalSortConfig, setInternalSortConfig] = useState<SortConfig | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [contextMenu, setContextMenu] = useState<ContextMenuState<T> | null>(null);
  const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);
  const [hoverPosition, setHoverPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [focusedRowIndex, setFocusedRowIndex] = useState<number | null>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // The callbacks a caller rebuilds on each of its own renders, read at event time instead of
  // captured, so the row handlers below can stay stable and the memoized rows stay mounted.
  const latestProps = useRef({ onRowClick, onRowModClick, rowContextMenu, rowHoverContent, onCellRightClick, selection });
  useEffect(() => {
    latestProps.current = { onRowClick, onRowModClick, rowContextMenu, rowHoverContent, onCellRightClick, selection };
  });

  // Clear focus when clicking outside the table (but not on context menus, modals, or overlays)
  const clearFocusOnOutsideClick = useCallback((e: MouseEvent) => {
    const target = e.target as HTMLElement;
    if (
      tableContainerRef.current?.contains(target) ||
      target.closest('.fixed, .modal-backdrop, [role="dialog"]') ||
      !target.isConnected
    ) return;
    setFocusedRowIndex(null);
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', clearFocusOnOutsideClick);
    return () => document.removeEventListener('mousedown', clearFocusOnOutsideClick);
  }, [clearFocusOnOutsideClick]);

  const clearHoverTimer = useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  }, []);

  const handleRowMouseDown = useCallback((index: number) => {
    setFocusedRowIndex(index);
  }, []);

  const handleRowClick = useCallback((row: T, rowId: string | undefined, e: React.MouseEvent) => {
    const { onRowClick, onRowModClick, rowContextMenu, selection } = latestProps.current;
    if (selection && rowId) {
      selection.onToggleRow(rowId);
      return;
    }
    if ((e.metaKey || e.ctrlKey) && onRowModClick) {
      onRowModClick(row, e);
      return;
    }
    if (rowContextMenu) {
      setContextMenu({ row, position: { x: e.clientX, y: e.clientY } });
    }
    onRowClick?.(row, e);
  }, []);

  const handleRowMouseEnter = useCallback((row: T, index: number) => {
    if (latestProps.current.rowHoverContent?.(row)) {
      clearHoverTimer();
      hoverTimerRef.current = setTimeout(() => {
        setHoveredRowIndex(index);
      }, 1000);
    }
  }, [clearHoverTimer]);

  const handleRowMouseMove = useCallback((row: T, e: React.MouseEvent) => {
    if (!latestProps.current.rowHoverContent?.(row)) return;
    const containerRect = tableContainerRef.current?.getBoundingClientRect();
    if (containerRect) {
      setHoverPosition({
        x: e.clientX - containerRect.left,
        y: e.clientY - containerRect.top,
      });
    }
  }, []);

  const handleRowMouseLeave = useCallback(() => {
    clearHoverTimer();
    setHoveredRowIndex(null);
  }, [clearHoverTimer]);

  const handleToggleRow = useCallback((rowId: string) => {
    latestProps.current.selection?.onToggleRow(rowId);
  }, []);

  const handleCellRightClick = useCallback((row: T, columnKey: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    latestProps.current.onCellRightClick?.(row, columnKey, { x: e.clientX, y: e.clientY });
  }, []);

  // Use external sort config if server-side sorting
  const sortConfig = serverSort ? externalSortConfig : internalSortConfig;

  // Filter data based on search (only for client-side)
  const filteredData = searchable && !serverSort
    ? data.filter((row) =>
        Object.values(row).some((value) =>
          String(value).toLowerCase().includes(searchTerm.toLowerCase())
        )
      )
    : data;

  // Sort data (only for client-side)
  const sortedData = !serverSort && sortConfig
    ? [...filteredData].sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      })
    : filteredData;

  const handleSort = (key: string) => {
    if (serverSort && onSort) {
      // Server-side: delegate to parent (asc -> desc -> clear)
      if (!sortConfig || sortConfig.key !== key) {
        onSort(key, 'asc');
      } else if (sortConfig.direction === 'asc') {
        onSort(key, 'desc');
      } else {
        onSort(key, null);
      }
    } else {
      // Client-side: manage internally
      setInternalSortConfig((current) => {
        if (!current || current.key !== key) {
          return { key, direction: 'asc' };
        }
        if (current.direction === 'asc') {
          return { key, direction: 'desc' };
        }
        return null;
      });
    }
  };

  // Checkbox header state
  const pageIds = selection ? sortedData.map((row) => selection.getRowId(row)) : [];
  const selectedOnPage = selection ? pageIds.filter((id) => selection.selectedIds.has(id)) : [];
  const allPageSelected = pageIds.length > 0 && selectedOnPage.length === pageIds.length;
  const somePageSelected = selectedOnPage.length > 0 && !allPageSelected;

  const rowsInteractive = !!(onRowClick || rowContextMenu || selection);

  return (
    <div className="flex flex-col h-full">
      {searchable && (
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-text-muted" />
          <Input
            type="text"
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      )}

      <div className="table-container flex-1 overflow-auto relative" ref={tableContainerRef}>
        <table className="table h-full">
          <thead className="table-header sticky top-0 z-10">
            <tr>
              {selection && (
                <th className="table-header-cell w-10">
                  <div className="flex items-center justify-center">
                    <input
                      type="checkbox"
                      checked={allPageSelected || !!selection.allPagesSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = somePageSelected && !selection.allPagesSelected;
                      }}
                      onChange={() => selection.onTogglePage(pageIds, allPageSelected)}
                      className="w-3.5 h-3.5 accent-primary-500 cursor-pointer"
                      title={allPageSelected ? 'Deselect all on page' : 'Select all on page'}
                    />
                  </div>
                </th>
              )}
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={clsx(
                    'table-header-cell group',
                    column.sortable && 'cursor-pointer select-none hover:bg-dark-border'
                  )}
                  onClick={() => column.sortable && handleSort(column.key)}
                  style={{ width: column.width }}
                >
                  <div className="flex items-center gap-2">
                    <span className="flex items-center gap-1">
                      {column.labelRender ? column.labelRender() : column.label}
                      {column.filterable && column.renderFilter
                        ? column.renderFilter()
                        : column.filterable && column.filterConfig && (
                            <ColumnFilter
                              values={column.filterConfig.values}
                              selectedValues={column.filterConfig.selectedValues}
                              onChange={column.filterConfig.onChange}
                            />
                          )}
                    </span>
                    {column.sortable && (
                      <div className="flex flex-col">
                        <ChevronUp
                          className={clsx(
                            'w-3 h-3 -mb-1',
                            sortConfig?.key === column.key &&
                              sortConfig.direction === 'asc'
                              ? 'text-primary-500'
                              : 'text-dark-text-muted'
                          )}
                        />
                        <ChevronDown
                          className={clsx(
                            'w-3 h-3',
                            sortConfig?.key === column.key &&
                              sortConfig.direction === 'desc'
                              ? 'text-primary-500'
                              : 'text-dark-text-muted'
                          )}
                        />
                      </div>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedData.length === 0 ? (
              <tr>
                <td
                  colSpan={selection ? columns.length + 1 : columns.length}
                  className="table-cell text-center py-12 text-dark-text-muted"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              sortedData.map((row, index) => {
                const rowId = selection?.getRowId(row);
                const isSelected = !!(rowId && selection && (selection.selectedIds.has(rowId) || selection.allPagesSelected));
                return (
                  <TableRow
                    // By id where there is one, so a refreshed page reuses rows per item
                    // rather than per position (and memoized rows survive the update).
                    key={rowId ?? (row._id as string | undefined) ?? index}
                    row={row}
                    index={index}
                    columns={columns}
                    compact={compact}
                    isSelected={isSelected}
                    isFocused={focusedRowIndex === index}
                    className={rowClassName?.(row)}
                    rowId={rowId}
                    interactive={rowsInteractive}
                    selectable={!!selection}
                    hoverable={!!rowHoverContent}
                    cellRightClickable={!!onCellRightClick}
                    onRowMouseDown={handleRowMouseDown}
                    onRowClick={handleRowClick}
                    onRowMouseEnter={handleRowMouseEnter}
                    onRowMouseMove={handleRowMouseMove}
                    onRowMouseLeave={handleRowMouseLeave}
                    onToggleRow={handleToggleRow}
                    onCellRightClick={handleCellRightClick}
                  />
                );
              })
            )}
          </tbody>
        </table>
        {hoveredRowIndex !== null && rowHoverContent && rowHoverContent(sortedData[hoveredRowIndex]) && (
          <div
            className="absolute z-20 px-3 py-2 text-xs text-dark-text-primary bg-dark-elevated border border-dark-border rounded-lg shadow-lg max-w-sm pointer-events-none"
            style={{
              left: `clamp(1rem, ${hoverPosition.x + 12}px, calc(100% - 1rem))`,
              top: hoverPosition.y,
              transform: 'translateY(-100%) translateY(-8px)',
            }}
          >
            {rowHoverContent(sortedData[hoveredRowIndex])}
          </div>
        )}
      </div>

      {contextMenu && rowContextMenu && (
        <ContextMenu
          items={rowContextMenu(contextMenu.row)}
          position={contextMenu.position}
          onClose={() => setContextMenu(null)}
        />
      )}
    </div>
  );
}
